import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { MailchimpIcon } from '@mm/icons';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import { FormattedConnectionModel } from '@mm/state/model/ConnectionModel';
import { MailchimpConnections } from '@mm/state/model/MailchimpModel';
import { selectMailchimpAccounts } from '@mm/state/selectors/mailchimpSelectors';
import { ConfigProvider, Table } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { IntegrationErrors } from '../../../constants';
import { ReactComponent as AddMailchimpIcon } from '../../../icons/add-mailchimp.svg';
import '../../IntegrationBaseComponent.scss';
import { MailchimpHeaderComponent } from './MailchimpHeaderComponent';

type Props = {
  isAccountConnected: boolean;
  onDisconnect: (id: number) => void;
  mailchimpCount: number;
  columns: ColumnType<FormattedConnectionModel>[];
  mailchimpConnections: MailchimpConnections[];
  name?: string;
};

export const IntegrationMailchimpComponent = (props: Props) => {
  const {
    isAccountConnected,
    onDisconnect,
    mailchimpCount,
    columns,
    mailchimpConnections,
    name,
  } = props;

  const accounts = useSelector(selectMailchimpAccounts);
  const { getMailchimpOauthUrl } = useMailchimpCRUDActions();
  const [oauthUrl, setOauthUrl] = useState('');
  const [haveErrors, setHaveErrors] = useState(false);

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  useEffect(() => {
    const getLink = async () => {
      const link = await getMailchimpOauthUrl(window.location.href);
      if (link) {
        setOauthUrl(link);
      }
    };
    getLink();
  }, []);

  useEffect(() => {
    const errorsString = mailchimpConnections
      .map(connection =>
        connection.connections.map(webhook =>
          webhook.webhookErrors?.map(error => error.errorDetails),
        ),
      )
      .flat()
      .join('');
    setHaveErrors(
      IntegrationErrors.some(error =>
        errorsString.toLowerCase().includes(error),
      ),
    );
  }, [mailchimpConnections]);

  const sectionProps = {
    landmineName: landmineNames?.INTEGRATIONS_MAILCHIMP,
    featureFlag: 'nativeIntegrations',
  };

  const headerComponent = (
    <MailchimpHeaderComponent
      isAccountConnected={isAccountConnected}
      mailchimpCount={mailchimpCount}
      accountsCount={accounts.length}
      oauthUrl={oauthUrl}
      name={name}
      accounts={accounts}
    />
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="Mailchimp"
          subtitle={`Automatically send data from ${companyName} to Mailchimp`}
          icon={<MailchimpIcon />}
          headerRightComponent={headerComponent}
          css={{ icon: 'integrations__mailchimp-header-icon' }}
          haveErrors={haveErrors}
          {...sectionProps}
        />
      }
      integrationType="mailchimp"
      showEmptyState={!mailchimpConnections.length}
      emptyStateConfig={{
        button: 'Add a Mailchimp Account',
        onClick: () => window.open(oauthUrl, '_self'),
        icon: <AddMailchimpIcon />,
        ...sectionProps,
      }}
    >
      {mailchimpConnections.map(connection => (
        <div key={`mailchimp-connection-${connection.accountId}`}>
          <div className="integrations-grid__account-header">
            <div className="integrations-grid__account-info">
              Mailchimp Account:{' '}
              <span className="integrations-grid___account-name">
                {connection.accountEmail
                  ? connection.accountEmail
                  : 'Not Assigned'}{' '}
              </span>
            </div>
            {accounts.find(
              acc => acc.id === connection.accountId && !acc.readOnly,
            ) && (
              <PrimaryButton
                className="integrations__header-add-button-secondary"
                onClick={() =>
                  onDisconnect(
                    Number(
                      accounts.find(acc => acc.id === connection.accountId)?.id,
                    ),
                  )
                }
                color="secondary-blue"
              >
                Disconnect
              </PrimaryButton>
            )}
          </div>
          {!!connection.connections.length && (
            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerBg: 'white',
                    borderColor: '#D9D9D9',
                    cellPaddingBlockSM: 3,
                  },
                },
              }}
            >
              <div className="integration-panel--ant-table">
                <Table
                  dataSource={connection.connections}
                  columns={columns}
                  pagination={false}
                  size="small"
                  bordered
                />
              </div>
            </ConfigProvider>
          )}
        </div>
      ))}
    </BaseIntegration>
  );
};
